// Persistência do catálogo de infraestrutura (migrations 0046 e 0048).
//
// O admin da plataforma mantém aqui o que existe no mundo — categorias, itens,
// periodicidade de manutenção, campos extras — e o cliente instancia. Quem pode
// escrever é o superusuário: as policies exigem `e_superusuario()`, e esta
// camada não tenta adivinhar isso. Se o banco recusar, o erro sobe.

import { supabase } from '@/lib/supabase';
import { invalidarCatalogo, type CampoExtra } from '@/lib/catalogo';
import { periodoParaColunas, type PeriodoParcial } from '@/lib/periodo';

export interface CategoriaDeInfraestrutura {
  id: string;
  nome: string;
  icone: string | null;
  cor: string;
  ordem: number;
  ativo: boolean;
}

export interface ItemDeCatalogo {
  id: string;
  nome: string;
  descricao: string;
  categoriaId: string;
  periodicidade: PeriodoParcial;
  camposExtras: CampoExtra[];
  ativo: boolean;
  /** Treinamentos que o item exige (N:N da secao 4.5). */
  treinamentos: string[];
}

/**
 * Id a partir do nome.
 *
 * Legível, como o resto do catálogo (`prof-porteiro`, `tmpl-001`): um uuid solto
 * numa tabela de configuração torna toda consulta manual um join a mais.
 */
export function idDoNome(prefixo: string, nome: string): string {
  const base = nome
    .normalize('NFD')
    // Tira os acentos que o NFD separou. `\p{Diacritic}` em vez de um intervalo
    // de combining marks: o intervalo escrito literalmente fica invisível no
    // editor, e ninguém consegue revisar o que não vê.
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  return `${prefixo}-${base || Date.now()}`;
}

// ─── Categorias ──────────────────────────────────────────────────────

export async function listarCategorias(): Promise<CategoriaDeInfraestrutura[]> {
  const { data, error } = await supabase
    .from('infraestrutura_categorias')
    .select('id, nome, icone, cor, ordem, ativo')
    .order('ordem');

  if (error) throw new Error(error.message);
  return (data ?? []) as CategoriaDeInfraestrutura[];
}

export async function salvarCategoria(c: CategoriaDeInfraestrutura): Promise<void> {
  const { error } = await supabase.from('infraestrutura_categorias').upsert(
    {
      id: c.id,
      nome: c.nome.trim(),
      icone: c.icone?.trim() || null,
      cor: c.cor,
      ordem: c.ordem,
      ativo: c.ativo,
      editado_em: new Date().toISOString(),
    },
    { onConflict: 'id' },
  );
  if (error) throw new Error(error.message);
  invalidarCatalogo('infraestrutura_categorias');
}

/**
 * Desativa a categoria.
 *
 * `.delete()` vira `deletado_em` pelo gatilho da migration 0019. Não é
 * cerimônia: itens do catálogo apontam para ela por foreign key, e uma exclusão
 * física deixaria o catálogo inconsistente ou seria recusada pelo banco no meio
 * da operação.
 */
export async function removerCategoria(id: string): Promise<void> {
  const { error } = await supabase.from('infraestrutura_categorias').delete().eq('id', id);
  if (error) throw new Error(error.message);
  invalidarCatalogo('infraestrutura_categorias');
  invalidarCatalogo('infraestrutura_itens');
}

// ─── Itens ───────────────────────────────────────────────────────────

export async function listarItens(): Promise<ItemDeCatalogo[]> {
  const [{ data, error }, { data: vinculos, error: erroVinculos }] = await Promise.all([
    supabase
      .from('infraestrutura_itens')
      .select(
        'id, nome, descricao, categoria_id, campos_extras, ativo, ' +
          'periodicidade_manutencao_valor, periodicidade_manutencao_unidade',
      )
      .order('nome'),
    supabase.from('infraestrutura_item_treinamentos').select('item_id, treinamento_id'),
  ]);

  if (error) throw new Error(error.message);
  if (erroVinculos) throw new Error(erroVinculos.message);

  const porItem = new Map<string, string[]>();
  for (const v of vinculos ?? []) {
    const atuais = porItem.get(v.item_id as string) ?? [];
    atuais.push(v.treinamento_id as string);
    porItem.set(v.item_id as string, atuais);
  }

  // `as unknown as` pelo mesmo motivo de `documentosRepo`: colunas vindas de
  // uma constante concatenada nao sao inferidas pelo cliente do Supabase.
  return ((data ?? []) as unknown as Record<string, unknown>[]).map((linha) => ({
    id: linha.id as string,
    nome: linha.nome as string,
    descricao: (linha.descricao as string) ?? '',
    categoriaId: linha.categoria_id as string,
    periodicidade: {
      valor: (linha.periodicidade_manutencao_valor as number) ?? null,
      unidade: (linha.periodicidade_manutencao_unidade as PeriodoParcial['unidade']) ?? null,
    },
    camposExtras: Array.isArray(linha.campos_extras) ? (linha.campos_extras as CampoExtra[]) : [],
    ativo: linha.ativo as boolean,
    treinamentos: porItem.get(linha.id as string) ?? [],
  }));
}

export async function salvarItem(item: ItemDeCatalogo): Promise<void> {
  const { error } = await supabase.from('infraestrutura_itens').upsert(
    {
      id: item.id,
      nome: item.nome.trim(),
      descricao: item.descricao.trim(),
      categoria_id: item.categoriaId,
      ...periodoParaColunas(item.periodicidade, 'periodicidade_manutencao'),
      // A constraint `infraestrutura_itens_campos_check` confere a forma no
      // banco. A limpeza aqui é para o caso comum — campo em branco que a
      // pessoa acrescentou e não preencheu — não virar erro de gravação.
      campos_extras: item.camposExtras.filter((c) => c.chave?.trim() && c.rotulo?.trim()),
      ativo: item.ativo,
      editado_em: new Date().toISOString(),
    },
    { onConflict: 'id' },
  );
  if (error) throw new Error(error.message);

  await substituirTreinamentosDoItem(item.id, item.treinamentos);

  invalidarCatalogo('infraestrutura_itens');
}

/**
 * Substitui o conjunto de treinamentos exigidos pelo item.
 *
 * Apaga o que saiu e grava o que ficou, como o repositório de profissões: o
 * conjunto é pequeno e precisa terminar exatamente igual ao que a tela mostra.
 * Ao contrário do vínculo treinamento↔profissão, aqui não há invariante a
 * defender — um item sem treinamento é um caso normal.
 */
async function substituirTreinamentosDoItem(itemId: string, treinamentos: string[]): Promise<void> {
  const { error: erroRemocao } = await supabase
    .from('infraestrutura_item_treinamentos')
    .delete()
    .eq('item_id', itemId);
  if (erroRemocao) throw new Error(erroRemocao.message);

  if (treinamentos.length === 0) return;

  const { error } = await supabase.from('infraestrutura_item_treinamentos').upsert(
    treinamentos.map((treinamento_id) => ({ item_id: itemId, treinamento_id })),
    { onConflict: 'item_id,treinamento_id' },
  );
  if (error) throw new Error(error.message);
}

/**
 * Desativa o item do catálogo.
 *
 * Instâncias já criadas continuam apontando para ele: `infraestrutura.item_catalogo_id`
 * tem `on delete set null`, e a exclusão lógica nem chega lá. O item some das
 * escolhas novas e o que já foi cadastrado continua sabendo de onde veio.
 */
export async function removerItem(id: string): Promise<void> {
  const { error } = await supabase.from('infraestrutura_itens').delete().eq('id', id);
  if (error) throw new Error(error.message);
  invalidarCatalogo('infraestrutura_itens');
}

// ─── Tipos de alvará: vínculo com itens (migration 0048) ─────────────

export interface TemplateDeAlvara {
  id?: string;
  nome: string;
  descricao: string;
  categoria: string;
  esfera: 'municipal' | 'estadual' | 'federal';
  abrangencia: string;
  estados: string[] | null;
  todasCidades: boolean;
  cidadesEspecificas: string[] | null;
  periodicidade: string;
  obrigatorio: boolean;
  orgaoEmissor: string;
  vigencia: PeriodoParcial;
  prazo: PeriodoParcial;
  requerDocumentoAnexo: boolean;
  diasAntecedenciaAviso: number | null;
  tempoMedioGeracao: PeriodoParcial;
  aplicaAoCondominioGeral: boolean;
  itens: string[];
  ativo: boolean;
}

/**
 * Grava o tipo de alvará e os itens vinculados na mesma transação.
 *
 * Passa por `salvar_alvara_template()` pelo mesmo motivo que o treinamento: a
 * constraint trigger exige que template e vínculos existam juntos, e duas
 * requisições PostgREST são duas transações.
 */
export async function salvarTemplateDeAlvara(t: TemplateDeAlvara): Promise<string> {
  const { data, error } = await supabase.rpc('salvar_alvara_template', {
    p_template: {
      id: t.id ?? null,
      nome: t.nome,
      descricao: t.descricao,
      categoria: t.categoria,
      esfera: t.esfera,
      abrangencia: t.abrangencia,
      estados: t.estados,
      todas_cidades: t.todasCidades,
      cidades_especificas: t.cidadesEspecificas,
      periodicidade: t.periodicidade,
      obrigatorio: t.obrigatorio,
      orgao_emissor: t.orgaoEmissor,
      ...periodoParaColunas(t.vigencia, 'vigencia'),
      ...periodoParaColunas(t.prazo, 'prazo'),
      requer_documento_anexo: t.requerDocumentoAnexo,
      dias_antecedencia_aviso: t.diasAntecedenciaAviso,
      ...periodoParaColunas(t.tempoMedioGeracao, 'tempo_medio_geracao'),
      aplica_ao_condominio_geral: t.aplicaAoCondominioGeral,
      itens: t.itens,
      ativo: t.ativo,
    },
  });

  if (error) throw new Error(error.message);

  invalidarCatalogo('alvaras_templates');
  invalidarCatalogo('categorias_alvara');
  invalidarCatalogo('infraestrutura_itens');
  return data as string;
}
